import { Button, typography } from '@joor/design-system'
import styled from 'styled-components'

import { useRenderToasters } from 'hooks/useRenderToasters'

import { useSendPaymentsInterestEmailMutation } from './emptyPayView.mutations'
import { PayTabIds } from 'features/Orders/Review/review.ids'
import { useOrderId } from 'features/Orders/orders.hooks'

type Props = {
  brandName?: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 50px;
`

const Header = styled.h2`
  && {
    ${typography.alpha.primaryActive.headline2}
    font-weight: var(--font-weight-light);
    line-height: 50px;
    color: var(--color-primary-800);
    margin: 0;
  }
`

const Subtitle = styled.h4`
  && {
    ${typography.alpha.primaryActive.headline4}
    line-height: 32px;
    color: var(--color-primary-800);
    text-align: center;
    margin: 0;
  }
`

const ExternalLink = styled.a`
  &&,
  &&:hover {
    color: var(--color-secondary-800);
  }
`

const StyledButton = styled(Button)`
  && {
    font-size: var(--size-font-small);
  }
`

export const EMPTY_PAY_VIEW_SENT_EMAIL_KEY = 'EmptyPayViewSentEmail'

export const BrandNotConnectedView = ({ brandName = 'The brand' }: Props) => {
  const orderId = useOrderId()
  const { sendPaymentsImInterestedEmail, loading } =
    useSendPaymentsInterestEmailMutation()
  const { renderSuccessToast, renderErrorToast } = useRenderToasters()

  const renderSendEmailError = () => {
    renderErrorToast({
      title: 'Error',
      description:
        'There was an error while processing your request, please try again later.',
    })
  }

  const handleSendImInterestedEmail = () => {
    sendPaymentsImInterestedEmail(orderId)
      .then((response) => {
        if (response.data?.sendJoorPayImInterestedEmail.success) {
          localStorage.setItem(
            EMPTY_PAY_VIEW_SENT_EMAIL_KEY,
            JSON.stringify(true),
          )
          renderSuccessToast({
            title: 'Success',
            description:
              "Thanks for letting us know you're interested in JOOR Pay.",
          })
        } else {
          renderSendEmailError()
        }
      })
      .catch(() => {
        renderSendEmailError()
      })
  }

  const isEmailSent = () => {
    const sentEmail = localStorage.getItem(EMPTY_PAY_VIEW_SENT_EMAIL_KEY)
    return sentEmail !== null ? JSON.parse(sentEmail) : false
  }

  return (
    <Container>
      <Header>Interested in paying online?</Header>
      <Subtitle>
        You can pay directly online when checking out with{' '}
        <ExternalLink href="https://www.joor.com/joor-pay" target="_blank">
          JOOR Pay
        </ExternalLink>{' '}
        enabled brands.
        <br />
        Let {brandName} know you're interested.
      </Subtitle>
      <StyledButton
        id={PayTabIds.RetailerInterestedId}
        type="button"
        onClick={handleSendImInterestedEmail}
        disabled={loading || isEmailSent()}
        {...(isEmailSent() ? { iconName: 'checkmark' } : {})}
      >
        {isEmailSent() ? 'Received' : "I'm interested"}
      </StyledButton>
    </Container>
  )
}

export const PaymentOptionsUnavailableView = ({
  brandName,
}: {
  brandName?: string
}) => {
  return (
    <Container>
      <Header>Payment options are not available</Header>
      <Subtitle>
        {brandName} hasn't shared the payment request with you yet.
        <br />
        Reach out to your brand contact directly to enable payment on JOOR for
        this order.
      </Subtitle>
    </Container>
  )
}

export const BrandJOORPayDisabledView = () => {
  const link =
    'https://withreach.withpersona.com/verify?inquiry-template-id=itmpl_GtnCmr3EfnPA354gWFffDGoKrskp&environment-id=' +
    'env_46UzWaKRhzrWdQsqYsigHRswnJt4&account-type-id=acttp_ya34WR4MUmq3TZSyC4U9MvtSVG7T'
  return (
    <Container>
      <Header>Receive Payments Faster & Easier with JOOR Pay</Header>
      <Subtitle>
        Want to handle your order from creation to final payment on JOOR?
      </Subtitle>
      <Subtitle>
        Track orders and payment status in one place and release orders to
        shipment faster.
      </Subtitle>
      <Subtitle>Enable JOOR Pay today at no up-front cost to you!</Subtitle>
      <StyledButton type="button" onClick={() => window.open(link, '_blank')}>
        Get Started
      </StyledButton>
    </Container>
  )
}
